import { TableScroll } from '@web/components/table-scroll.tsx';
import { useReportPaging } from '@web/components/report-pagination.tsx';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { Alert, EmptyState, classes } from '@web/components/ui.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { formatMinutes } from '@web/i18n/format.ts';
import { getDateLocale } from '@web/i18n/locale.ts';
import { reportRetry, ReportBoundary, SectionCard, TH, THR, TD, TDR, ROW, type RangeParams } from '../shared.tsx';

/**
 * Margin per site per month — the report a janitorial owner scans for
 * negatives before a renewal comes up.
 *
 * Ordered by month, newest first, the way the query returns it: the question
 * is "what has changed lately", and a table sorted by margin would bury the
 * building that was fine until March.
 */
export function JobCostingSection({ range, scope }: { range: RangeParams; scope?: 'all' }) {
  const { t } = useTranslation();
  const paging = useReportPaging({ ...range, scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'job-costing', { ...range, scope, pages }, paging.identity],
    queryFn: () => reportsApi.jobCosting({ ...range, scope, pages }),
    retry: reportRetry,
  });

  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => {
        const money = (cents: number) => formatCents(cents, report.currencyCode, getDateLocale());
        return (
          <>
            {/*
              Said once, at the top, rather than as a dash in every margin
              cell. Team pay can cost the rows with the hourly backup blank.
              The note is for the case where neither one can: no backup, and
              every row (or an empty report) has no loaded labor cost.
            */}
            {report.laborCostCentsPerHour === null &&
            (report.rows.length === 0 || report.rows.every((row) => row.laborCostCents === null)) ? (
              <Alert tone="info" title={t('reports.jobCosting.noCostTitle')}>
                {t('reports.jobCosting.noCostBody')}
              </Alert>
            ) : null}

            {report.rows.length === 0 && (report.pagination?.rows?.page ?? 1) === 1 ? (
              <EmptyState title={t('reports.jobCosting.emptyTitle')}>{t('reports.jobCosting.emptyBody')}</EmptyState>
            ) : (
              <SectionCard
                title={t('reports.jobCosting.title')}
                collection="rows"
                hint={t('reports.jobCosting.hint')}
                csvHref={reportsApi.csvUrl('job-costing', { ...range, scope })}
              >
                <TableScroll aria-label={t('reports.jobCosting.title')} className="overflow-x-auto">
                  <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-[color:var(--color-line)]">
                        <th scope="col" className={TH}>
                          {t('reports.jobCosting.colMonth')}
                        </th>
                        <th scope="col" className={TH}>
                          {t('reports.jobCosting.colSite')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobCosting.colVisits')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobCosting.colHours')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobCosting.colRevenue')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobCosting.colCost')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobCosting.colMargin')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((row) => (
                        <tr
                          key={`${row.month}:${row.propertyId ?? row.customerId}`}
                          className={classes(ROW, row.losingMoney && 'bg-[color:var(--color-danger-soft)]')}
                        >
                          <td className={TD}>{row.month}</td>
                          <td className={TD}>
                            <span className="block max-w-[18rem] table-text">{row.siteName || row.customerName}</span>
                            <span className="block max-w-[18rem] table-text text-xs text-[color:var(--color-muted)]">
                              {row.customerName}
                              {row.siteCode ? ` · ${row.siteCode}` : ''}
                            </span>
                          </td>
                          <td className={TDR}>
                            {/* Costed against completed: how much of the month the answer covers. */}
                            {t('reports.jobCosting.visitsCosted', {
                              costed: row.trackedVisits,
                              total: row.completedVisits,
                            })}
                          </td>
                          <td className={TDR}>
                            {/*
                              The hours the cost is charged against: everyone's
                              time, not the visit's span. On a crewed site the
                              two differ, and the span is what the reader
                              recognizes from the schedule — so it stays
                              visible underneath rather than being replaced by
                              a bigger number with no explanation.
                            */}
                            {formatMinutes(row.laborMinutes)}
                            {row.laborMinutes !== row.actualMinutes ? (
                              <span className="block text-xs text-[color:var(--color-muted)]">
                                {t('reports.jobCosting.onSite', { time: formatMinutes(row.actualMinutes) })}
                              </span>
                            ) : null}
                          </td>
                          <td className={TDR}>{money(row.revenueCents)}</td>
                          <td className={TDR}>{row.laborCostCents === null ? '—' : money(row.laborCostCents)}</td>
                          <td className={TDR}>
                            {row.marginCents === null ? (
                              '—'
                            ) : (
                              <>
                                {money(row.marginCents)}
                                {row.marginBasisPoints !== null ? (
                                  <span className="block text-xs text-[color:var(--color-muted)]">
                                    {(row.marginBasisPoints / 100).toFixed(1)}%
                                  </span>
                                ) : null}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
                {report.truncated ? (
                  <p className="mt-2 text-sm text-[color:var(--color-muted)]">
                    {t('reports.jobCosting.truncated', { count: report.rows.length })}
                  </p>
                ) : null}
              </SectionCard>
            )}
          </>
        );
      }}
    </ReportBoundary>
  );
}
